import { AnyAction, Reducer } from 'redux';
import {
  GlobalAction,
  GlobalActions,
  ProduktDeleted,
  ReplaceState,
  WrappedSavedStates,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import cloneDeep from 'lodash/cloneDeep';
import { IAppState } from '../appState';
import { INITIAL_STATE } from '../initialState';
import { loadAndMigrateTgvState } from '../migrations';
import { childKeyOf, listedChildKeys, sameKeys } from '../models/childKeys';
import { tiereWithoutDeletedOffers } from '../models/deletedOffers';
import { firstSelectableKey, selectableChildKeys } from '../models/angebotOffers';
import { TgvActions } from '../tgvActions';
import { withTiere, withTkvOnly } from './tiergruppe.reducer';

const withoutAngebotKeys = (reduced: IAppState, deletedKeys: string[]): IAppState => {
  const { current, editedKey, visited } = reduced.transientState.angebot;
  const { pricing } = reduced.transientState;
  const currentDeleted = current !== null && deletedKeys.includes(current);
  const editedDeleted = editedKey !== null && deletedKeys.includes(editedKey);
  if (
    deletedKeys.length === 0 ||
    (!currentDeleted &&
      !editedDeleted &&
      !visited.some(key => deletedKeys.includes(key)) &&
      !deletedKeys.some(key => Object.hasOwn(pricing, key)))
  ) {
    return reduced;
  }
  const nextCurrent = currentDeleted
    ? firstSelectableKey(reduced.tiergruppe, reduced.transientState)
    : current;
  const remainingVisited = visited.filter(key => !deletedKeys.includes(key));
  return {
    ...reduced,
    transientState: {
      ...reduced.transientState,
      pricing: Object.fromEntries(
        Object.entries(pricing).filter(([key]) => !deletedKeys.includes(key)),
      ),
      angebot: {
        ...reduced.transientState.angebot,
        current: nextCurrent,
        editedKey: editedDeleted ? null : editedKey,
        visited: remainingVisited,
      },
    },
  };
};

const settledAngebot = (reduced: IAppState): IAppState => {
  const { current, editedKey } = reduced.transientState.angebot;
  const selectable = selectableChildKeys(reduced.tiergruppe, reduced.transientState);
  const preferred = selectable.includes(editedKey) ? editedKey : current;
  const nextCurrent =
    preferred !== null && selectable.includes(preferred) ? preferred : (selectable[0] ?? null);
  return {
    ...reduced,
    transientState: {
      ...reduced.transientState,
      angebot: {
        ...reduced.transientState.angebot,
        current: nextCurrent,
        editedKey: null,
      },
    },
  };
};

const REGISTRY_ACTIONS: ReadonlySet<string> = new Set([
  GlobalActions.OWNED_STORE_INITIALIZED,
  GlobalActions.PRODUKT_DELETED,
  GlobalActions.ALL_MISSING_STORES_CREATED,
]);

const withRegistryVersion =
  (inner: (state: IAppState, action: AnyAction) => IAppState) =>
  (state: IAppState, action: AnyAction): IAppState => {
    const next = inner(state, action);
    if (!state?.tiergruppe || !next?.transientState) {
      return next;
    }
    const bump =
      REGISTRY_ACTIONS.has(action.type) ||
      !sameKeys(listedChildKeys(state.tiergruppe), listedChildKeys(next.tiergruppe));
    const registryVersion = (state.transientState?.children?.registryVersion ?? 0) + (bump ? 1 : 0);
    if (next.transientState.children?.registryVersion === registryVersion) {
      return next;
    }
    return {
      ...next,
      transientState: {
        ...next.transientState,
        children: { ...next.transientState.children, registryVersion },
      },
    };
  };

const needsTkvOnly = (state: IAppState): boolean =>
  state?.transientState?.juristisch === true &&
  (state.tiergruppe.tiere.some(animal => animal.umfang.thh) ||
    state.tiergruppe.positions.thhHund !== null ||
    state.tiergruppe.positions.thhPferd !== null);

const withTkvOnlyForJuristisch =
  (inner: (state: IAppState, action: AnyAction) => IAppState) =>
  (state: IAppState, action: AnyAction): IAppState => {
    const next = inner(state, action);
    if (!needsTkvOnly(next)) {
      return next;
    }
    return { ...next, tiergruppe: withTkvOnly(next.tiergruppe) };
  };

const baseRootReducer =
  (reducer: Reducer) =>
  (state: IAppState, action: AnyAction): IAppState => {
    switch (action.type) {
      case GlobalActions.REPLACE_STATE: {
        const actionPayload: ReplaceState<IAppState> = action.payload;
        if (actionPayload.appId === App.TGV) {
          const { angebotsIds, thhAngelegt, thhVorvertrag, uebernommen } = state.tiergruppe;
          const { children, pricing, angebot, eingabeTarget, pricingIssue } = state.transientState;
          return {
            ...actionPayload.appState,
            tiergruppe: {
              ...actionPayload.appState.tiergruppe,
              angebotsIds,
              thhAngelegt,
              thhVorvertrag,
              uebernommen,
            },
            transientState: {
              ...actionPayload.appState.transientState,
              juristisch: state.transientState.juristisch,
              children,
              takeOverRunning: state.transientState.takeOverRunning,
              pricing,
              angebot,
              eingabeTarget,
              pricingIssue,
            },
          };
        }
        return state;
      }
      case GlobalActions.PRODUKT_DELETED: {
        const actionPayload: ProduktDeleted = action.payload;
        if (actionPayload.angebotsIds.some(angebot => angebot.appId === App.TGV)) {
          // The ids stay: the children are deleted with the group (see deleteGroupSaga), and their
          // PRODUKT_DELETED removes the ids.
          const { angebotsIds, thhAngelegt, kunde } = state.tiergruppe;
          const childKeys = listedChildKeys(state.tiergruppe);
          const transientState: IAppState['transientState'] = {
            ...state.transientState,
            showErrors: INITIAL_STATE.transientState.showErrors,
            runFailure: INITIAL_STATE.transientState.runFailure,
            angebot: cloneDeep(INITIAL_STATE.transientState.angebot),
            eingabeTarget: null,
            children:
              childKeys.length === 0
                ? state.transientState.children
                : {
                    ...state.transientState.children,
                    pendingDeletion: [
                      ...state.transientState.children.pendingDeletion,
                      ...childKeys.filter(
                        key => !state.transientState.children.pendingDeletion.includes(key),
                      ),
                    ],
                  },
          };
          return {
            ...cloneDeep(INITIAL_STATE),
            tiergruppe: { ...cloneDeep(INITIAL_STATE.tiergruppe), angebotsIds, thhAngelegt, kunde },
            transientState,
          };
        }
        const deletedKeys = actionPayload.angebotsIds
          .map(childKeyOf)
          .filter((key): key is string => key !== null);
        const reduced: IAppState = reducer(state, action);
        const { creating, pendingDeletion } = state.transientState.children;
        const tiere = tiereWithoutDeletedOffers(state.tiergruppe, actionPayload.angebotsIds, [
          ...creating,
          ...pendingDeletion,
        ]);
        return withoutAngebotKeys(
          tiere === state.tiergruppe.tiere
            ? reduced
            : { ...reduced, tiergruppe: withTiere(reduced.tiergruppe, tiere) },
          deletedKeys,
        );
      }
      case TgvActions.TGV_CHILD_LINKS_HEALED:
        return withoutAngebotKeys(reducer(state, action), action.payload ?? []);
      case TgvActions.TGV_ANGEBOTS_ID_CLEARED: {
        const clearedId = state.tiergruppe.angebotsIds[action.payload];
        return withoutAngebotKeys(
          reducer(state, action),
          clearedId === undefined ? [] : [clearedId],
        );
      }
      case TgvActions.TGV_THH_CLEARED:
        return withoutAngebotKeys(reducer(state, action), [
          action.payload === 'thhHund' ? App.THH_HUND : App.THH_PFERD,
        ]);
      case TgvActions.TGV_ANGEBOT_OPENED:
        return settledAngebot(reducer(state, action));
      case GlobalActions.SAVED_STATES_LOADED: {
        const actionPayload: WrappedSavedStates = (action as GlobalAction<WrappedSavedStatesLoaded>)
          .payload.savedStatesLoaded;
        const loadedAppState = actionPayload.find(loadedState => loadedState.appId === App.TGV);
        if (loadedAppState != null) {
          const loaded = loadAndMigrateTgvState(
            cloneDeep({
              ...state,
              transientState: {
                ...state.transientState,
                juristisch: INITIAL_STATE.transientState.juristisch,
              },
            }),
            loadedAppState.state,
          );
          return {
            ...loaded,
            transientState: {
              ...loaded.transientState,
              angebot: cloneDeep(INITIAL_STATE.transientState.angebot),
            },
          };
        }
        return state;
      }
      default:
        return reducer(state, action);
    }
  };

export const rootReducer = (
  reducer: Reducer,
): ((state: IAppState, action: AnyAction) => IAppState) =>
  withRegistryVersion(withTkvOnlyForJuristisch(baseRootReducer(reducer)));
